<template>
  <div class="permissions-tree">
    <v-container class="pa-4">
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="search"
            hide-details
            clearable
            placeholder="Search in permissions"
            outlined
            dense
            prepend-inner-icon="mdi-magnify">
          </v-text-field>
        </v-col>
        
        <!-- Permission Stats -->
        <v-col cols="12" class="pt-2 pb-0">
          <div class="d-flex align-center justify-space-between">
            <div class="caption grey--text">
              <span class="font-weight-medium">{{ selectedPermissionsCount }}</span> selected of 
              <span class="font-weight-medium">{{ totalPermissionsCount }}</span> permissions
              <span v-if="!companyId || companyId === 'new'" class="error--text ml-2">
                <v-icon x-small color="error">mdi-asterisk</v-icon>
                Required
              </span>
            </div>
            <div>
              <v-chip x-small color="primary" outlined class="mr-1">
                <v-icon left x-small>mdi-folder</v-icon>
                {{ parentNodesCount }} Selected Modules
              </v-chip>
            </div>
          </div>
        </v-col>
        
        <!-- Toolbar -->
        <v-col cols="12" class="py-1">
          <div class="d-flex align-center justify-space-between">
            <v-btn-toggle
              v-model="activeFilter"
              dense
              mandatory
              class="elevation-0"
              style="border: 1px solid rgba(0,0,0,0.12); border-radius: 4px;"
            >
              <v-btn small value="all" text>
                <v-icon left small>mdi-view-list</v-icon>
                All
              </v-btn>
              <v-btn small value="selected" text>
                <v-icon left small>mdi-check-circle-outline</v-icon>
                Selected 
              </v-btn>
              <v-btn small value="unselected" text>
                <v-icon left small>mdi-circle-outline</v-icon>
                Unselected 
              </v-btn>
              <v-btn small value="top-level" text>
                <v-icon left small>mdi-folder-multiple</v-icon>
                Top Level Only
              </v-btn>
            </v-btn-toggle>
            
            <div class="d-flex align-center">
              <v-btn
                small
                outlined
                :color="hasVisibleSelections ? 'grey' : 'primary'"
                class="mr-2"
                @click="toggleSelectAll"
                :disabled="loading"
              >
                <v-icon left small>{{ hasVisibleSelections ? 'mdi-checkbox-blank-outline' : 'mdi-checkbox-marked-outline' }}</v-icon>
                {{ hasVisibleSelections ? 'Deselect All' : 'Select All' }}
              </v-btn>
              
              <v-btn
                small
                outlined
                color="info"
                class="mr-3"
                @click="toggleExpandAll"
                :disabled="loading"
              >
                <v-icon left small>{{ allExpanded ? 'mdi-collapse-all' : 'mdi-expand-all' }}</v-icon>
                {{ allExpanded ? 'Collapse All' : 'Expand All' }}
              </v-btn>
              
              <v-btn
                color="primary"
                small
                :disabled="!hasChanges"
                @click="savePermissions"
                :loading="saving"
              >
                <v-icon left small>mdi-content-save</v-icon>
                {{ companyId && companyId !== 'new' ? 'Save' : 'Apply' }}
              </v-btn>
            </div>
          </div>
        </v-col>
        
        <v-col cols="12">
          <div style="border-radius: 0.5rem" class="pa-2 tree-wrapper">
            <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 300px;">
              <v-progress-circular
                color="primary"
                :width="2"
                :rotate="-90"
                :size="24"
                indeterminate></v-progress-circular>
            </div>
            
            <template v-else>
              <v-treeview
                v-if="filteredPermissionsTree && filteredPermissionsTree.length"
                :key="`tree-${companyId}-${activeFilter}-${selectedPermissions.length}`"
                v-model="treeSelectedPermissions"
                :items="filteredPermissionsTree"
                item-key="id"
                open-on-click
                selectable
                :return-object="false"
                :open="openNodes"
                selected-color="primary"
                @update:open="updateOpenNodes"
                :search="search"
                hoverable
                transition
                selection-type="leaf"
                @input="onSelectionChange"
                @update:active="onTreeUpdate"
              >
                <template #prepend="{ item, open, selected, indeterminate }">
                  <!-- Leaf node: key icon -->
                  <v-icon
                    v-if="item.isLeaf"
                    style="font-size: 1.3rem"
                    :class="
                      selected
                        ? 'fad primary--text'
                        : indeterminate
                        ? 'fad primary--text'
                        : 'fad grey--text'
                    ">
                    mdi-key
                  </v-icon>
                  <!-- Level 1 grouping node -->
                  <v-icon
                    v-else-if="isGroupingNode(item)"
                    style="font-size: 1.3rem"
                    :class="
                      selected
                        ? 'fad primary--text'
                        : indeterminate
                        ? 'fad primary--text'
                        : 'fad grey--text'
                    ">
                    {{ open ? 'mdi-package-variant-closed' : 'mdi-package-variant' }}
                  </v-icon>
                  <!-- Level 2+ folder nodes -->
                  <v-icon
                    v-else
                    :class="
                      selected
                        ? 'fad primary--text'
                        : indeterminate
                        ? 'fad primary--text'
                        : 'fad grey--text'
                    "
                    style="font-size: 1.3rem">
                    {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                  </v-icon>
                </template>

                <template #label="{ item, selected, indeterminate }">
                  <span
                    :class="[
                      selected
                        ? 'primary--text'
                        : indeterminate
                        ? 'primary--text'
                        : 'grey--text',
                      isGroupingNode(item) ? 'font-weight-bold' : ''
                    ]"
                    style="font-size: 1rem">
                    {{ isGroupingNode(item) ? item.name : $splitCamelCase(item.name) }}
                    <v-chip
                      v-if="item.isSystem"
                      x-small
                      class="ml-2"
                      color="red"
                      text-color="white"
                      style="font-size: 10px"
                    >
                      System
                    </v-chip>
                  </span>
                </template>
              </v-treeview>
              
              <div v-else class="pa-16 d-flex align-center justify-center flex-column">
                <v-img max-height="350px" contain src="/img/notFound.svg"></v-img>
                <div class="pt-4 title">No Permissions Found</div>
              </div>
            </template>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import permissionsService from '@Company/service/permissions.service';
import { $splitCamelCase } from '@/utils';
import { log, warn, error } from '@/utils/debugLogger';

const MAIN_GROUPING_ORDER = [
  'View & Navigation',
  'Data & Docs',
  'Management',
  'Integrity',
  'Facility Admin',
  'Admin'
];

export default {
  name: 'permissions-tree',
  props: {
    companyId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      permissionsTree: [],
      filteredPermissionsTree: [],
      search: '',
      selectedPermissions: [],
      treeSelectedPermissions: [],
      originalSelectedPermissions: [],
      activeFilter: 'all',
      allPermissionIds: [],
      flattenedPermissions: {},
      saving: false,
      allExpanded: true,
      openNodes: []
    };
  },
  computed: {
    selectedPermissionsCount() {
      return this.selectedPermissions.length;
    },
    totalPermissionsCount() {
      return this.allPermissionIds.length;
    },
    parentNodesCount() {
      // Always count selected parent nodes regardless of filter
      return this.countSelectedParentNodes(this.permissionsTree);
    },
    hasChanges() {
      // For new companies, consider any selection as a change
      if (!this.companyId || this.companyId === 'new') {
        return this.selectedPermissions.length > 0;
      }
      
      // For existing companies, check if current selections differ from original
      if (this.selectedPermissions.length !== this.originalSelectedPermissions.length) {
        return true;
      }
      return !this.selectedPermissions.every(id => this.originalSelectedPermissions.includes(id));
    },
    // Ensure selected permissions are valid tree IDs
    validSelectedPermissions() {
      return this.selectedPermissions.filter(id => this.allPermissionIds.includes(id));
    },
    // Check if there are any selections in the currently visible tree
    hasVisibleSelections() {
      const visiblePermissionIds = this.getFilteredPermissionIds();
      return visiblePermissionIds.some(id => this.selectedPermissions.includes(id));
    }
  },
  watch: {
    companyId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchPermissionsTree();
        }
      }
    },
    selectedPermissions: {
      handler() {
        this.applyFilters();
        this.$nextTick(() => {
          this.syncTreeSelections();
        });
      },
      deep: true
    },
    activeFilter: {
      handler() {
        this.applyFilters();
        this.$nextTick(() => {
          this.syncTreeSelections();
        });
      }
    },
    permissionsTree: {
      handler(newVal) {
        if (newVal && newVal.length) {
          this.initializeOpenNodes();
          this.applyFilters();
        }
      },
      deep: true
    }
  },
  methods: {
    $splitCamelCase,
    isGroupingNode(item) {
      return item.id && item.id.startsWith('grouping::');
    },
    async fetchPermissionsTree() {
      if (!this.companyId) return;
      this.loading = true;
      
      try {
        const defaultResponse = await permissionsService.getDefaultPermissions(this.companyId);
        
        this.permissionsTree = this.buildTreeFromFlatList(defaultResponse.data || []);
        this.extractAllPermissionIds();
        
        if (this.companyId && this.companyId !== 'new') {
          const selectedResponse = await permissionsService.getPermissions(this.companyId);
          const selectedPermissions = selectedResponse.data || [];
          if (Array.isArray(selectedPermissions)) {
            this.selectedPermissions = selectedPermissions.map(p => p.name);
          } else {
            this.selectedPermissions = [];
          }
          
        } else {
          this.selectedPermissions = [];
        }
        
        this.originalSelectedPermissions = [...this.selectedPermissions];
        
        this.initializeOpenNodes();
        
        this.$nextTick(() => {
          this.synchronizeSelections();
          this.applyFilters();
          this.$nextTick(() => {
            this.syncTreeSelections();
          });
        });
        
      } catch (err) {
        error('Error fetching permissions:', err);
        this.$dialog.notify.error('Failed to load permissions');
      } finally {
        this.loading = false;
      }
    },
    buildTreeFromFlatList(flatPermissions) {
      this.flattenedPermissions = {};

      // Step 1: Group permissions by mainGrouping
      const groupingMap = {};

      flatPermissions.forEach(permission => {
        const permissionName = permission.name;
        if (!permissionName) {
          warn('Permission without name:', permission);
          return;
        }

        const mainGrouping = permission.mainGrouping || 'Other';
        if (!groupingMap[mainGrouping]) {
          groupingMap[mainGrouping] = [];
        }
        groupingMap[mainGrouping].push(permission);
      });

      // Step 2: Sort groupings by recommended display order
      const sortedGroupings = Object.keys(groupingMap).sort((a, b) => {
        const indexA = MAIN_GROUPING_ORDER.indexOf(a);
        const indexB = MAIN_GROUPING_ORDER.indexOf(b);
        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });

      // Step 3: Build tree with mainGrouping as Level 1
      const tree = [];

      sortedGroupings.forEach(grouping => {
        const groupingNode = {
          id: `grouping::${grouping}`,
          name: grouping,
          fullPath: grouping,
          children: [],
          isLeaf: false,
          originalPermission: null,
          isSystem: false
        };

        // Build sub-tree for permissions within this grouping
        const nodeMap = {};

        groupingMap[grouping].forEach(permission => {
          const permissionName = permission.name;
          const parts = permissionName.split('.');
          let currentPath = grouping;

          parts.forEach((part, index) => {
            const isLast = index === parts.length - 1;
            currentPath = `${currentPath}.${part}`;

            if (!nodeMap[currentPath]) {
              const nodeId = isLast
                ? (permission.id || permission._id || permissionName).toString()
                : currentPath;

              nodeMap[currentPath] = {
                id: nodeId,
                name: part,
                fullPath: currentPath,
                children: [],
                isLeaf: isLast,
                originalPermission: isLast ? permission : null,
                isSystem: permission.isSystem || false
              };

              if (isLast) {
                this.flattenedPermissions[nodeId] = nodeMap[currentPath];
              }
            }
          });
        });

        // Link nodes: Level 2 nodes attach to groupingNode, deeper nodes attach to parents
        Object.values(nodeMap).forEach(node => {
          const pathParts = node.fullPath.split('.');
          // pathParts[0] = grouping name, pathParts[1] = Level 2, etc.

          if (pathParts.length === 2) {
            // Level 2 — direct child of grouping
            if (!groupingNode.children.find(child => child.id === node.id)) {
              groupingNode.children.push(node);
            }
          } else {
            // Level 3+ — find parent in nodeMap
            const parentPath = pathParts.slice(0, -1).join('.');
            const parent = nodeMap[parentPath];

            if (parent && !parent.children.find(child => child.id === node.id)) {
              parent.children.push(node);
            }
          }
        });

        tree.push(groupingNode);
      });

      return tree;
    },
    extractAllPermissionIds() {
      // Extract all permission IDs for counting and filtering
      this.allPermissionIds = [];
      const extractIds = (nodes) => {
        if (!nodes) return;
        
        nodes.forEach(node => {
          if (node.isLeaf) {
            // This is a leaf node (permission)
            this.allPermissionIds.push(node.id.toString());
          }
          
          if (node.children && node.children.length > 0) {
            extractIds(node.children);
          }
        });
      };
      
      extractIds(this.permissionsTree);
    },
    countParentNodes(nodes) {
      if (!nodes) return 0;
      
      let count = 0;
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          count++;
          count += this.countParentNodes(node.children);
        }
      });
      
      return count;
    },
    countSelectedParentNodes(nodes) {
      if (!nodes) return 0;
      
      let count = 0;
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          // Check if this parent node has any selected children
          const hasSelectedChildren = this.hasSelectedChildren(node);
          if (hasSelectedChildren) {
            count++;
          }
          count += this.countSelectedParentNodes(node.children);
        }
      });
      
      return count;
    },
    hasSelectedChildren(node) {
      if (!node.children || node.children.length === 0) {
        return this.selectedPermissions.includes(node.id.toString());
      }
      
      return node.children.some(child => {
        if (child.children && child.children.length > 0) {
          return this.hasSelectedChildren(child);
        }
        return this.selectedPermissions.includes(child.id.toString());
      });
    },
    onSelectionChange(newSelection) {
      
      // Convert all selections to strings for consistency
      const normalizedSelection = newSelection.map(id => id.toString());
      
      // When in unselected filter, merge with existing selections
      if (this.activeFilter === 'unselected') {
        // Find newly selected items from unselected filter
        const newlySelected = normalizedSelection.filter(id => !this.selectedPermissions.includes(id));
        // Find deselected items (items that were in tree but not in new selection)
        const currentFilteredIds = this.getFilteredPermissionIds();
        const deselected = currentFilteredIds.filter(id => !normalizedSelection.includes(id));
        
        // Update main selections: add newly selected, remove deselected
        let updatedSelections = [...this.selectedPermissions];
        newlySelected.forEach(id => {
          if (!updatedSelections.includes(id)) {
            updatedSelections.push(id);
          }
        });
        deselected.forEach(id => {
          const index = updatedSelections.indexOf(id);
          if (index > -1) {
            updatedSelections.splice(index, 1);
          }
        });
        
        this.selectedPermissions = updatedSelections;
      } else {
        // For other filters, update selections normally but preserve selections from other filters
        const currentFilteredIds = this.getFilteredPermissionIds();
        const selectionsFromOtherFilters = this.selectedPermissions.filter(id => !currentFilteredIds.includes(id));
        this.selectedPermissions = [...selectionsFromOtherFilters, ...normalizedSelection];
      }
      this.$emit('selection-change', this.selectedPermissions);
    },
    onTreeUpdate() {
      // Handle tree updates
    },
    async savePermissions() {
      this.saving = true;
      
      try {
        // Only call the API if we have a valid company ID (not 'new' or empty)
        if (this.companyId && this.companyId !== 'new') {
          // Call the API to update permissions
          await permissionsService.updatePermission(this.companyId, {
            permissions: this.selectedPermissions
          });
          
          this.$dialog.notify.success('Permissions updated successfully!', {
            position: 'top-right',
            timeout: 3000,
          });
        }  
        // Emit save event with current selections in all cases
        this.$emit('save-permissions', this.selectedPermissions);
        
        // Update original selections to current state
        this.originalSelectedPermissions = [...this.selectedPermissions];
        
      } catch (err) {
        error('Error saving permissions:', err);
        this.$dialog.notify.error('Failed to save permissions');
      } finally {
        this.saving = false;
      }
    },
    synchronizeSelections() {
      // Filter out any selected permissions that don't exist in the tree
      const validSelections = this.selectedPermissions.filter(id => this.allPermissionIds.includes(id));
      
      if (validSelections.length !== this.selectedPermissions.length) {
        this.selectedPermissions = validSelections;
        this.originalSelectedPermissions = [...validSelections];
      }
      
    },
    applyFilters() {
      if (!this.permissionsTree || !this.permissionsTree.length) {
        this.filteredPermissionsTree = [];
        return;
      }
      
      // Clone the original tree to avoid modifying it
      const clonedTree = JSON.parse(JSON.stringify(this.permissionsTree));
      
      switch (this.activeFilter) {
        case 'selected':
          this.filteredPermissionsTree = this.filterSelectedPermissions(clonedTree);
          break;
        case 'unselected':
          this.filteredPermissionsTree = this.filterUnselectedPermissions(clonedTree);
          break;
        case 'top-level':
          this.filteredPermissionsTree = this.filterTopLevelOnly(clonedTree);
          break;
        default: // 'all'
          this.filteredPermissionsTree = clonedTree;
          break;
      }
    },
    filterSelectedPermissions(tree) {
      if (!tree) return [];
      
      const result = [];
      
      tree.forEach(node => {
        const nodeClone = { ...node };
        
        if (node.isLeaf) {
          // Leaf node - include if selected
          if (this.selectedPermissions.includes(node.id.toString())) {
            result.push(nodeClone);
          }
        } else {
          // Parent node - recursively filter children
          const filteredChildren = this.filterSelectedPermissions(node.children);
          
          if (filteredChildren.length > 0) {
            nodeClone.children = filteredChildren;
            result.push(nodeClone);
          }
        }
      });
      
      return result;
    },
    filterUnselectedPermissions(tree) {
      if (!tree) return [];
      
      const result = [];
      
      tree.forEach(node => {
        const nodeClone = { ...node };
        
        if (node.isLeaf) {
          // Leaf node - include if NOT selected
          if (!this.selectedPermissions.includes(node.id.toString())) {
            result.push(nodeClone);
          }
        } else {
          // Parent node - recursively filter children
          const filteredChildren = this.filterUnselectedPermissions(node.children);
          
          if (filteredChildren.length > 0) {
            nodeClone.children = filteredChildren;
            result.push(nodeClone);
          }
        }
      });
      
      return result;
    },
    filterTopLevelOnly(tree) {
      if (!tree) return [];
      
      // Return only parent nodes (hide leaf nodes)
      const result = [];
      
      tree.forEach(node => {
        if (!node.isLeaf) {
          // This is a parent node - include it but remove its children
          result.push({
            ...node,
            children: undefined // Remove children completely for top-level view
          });
        }
      });
      
      return result;
    },
    syncTreeSelections() {
      // Sync tree selections based on current filter and selected permissions
      this.$nextTick(() => {
        if (this.activeFilter === 'unselected') {
          // For unselected filter, show empty selections initially
          this.treeSelectedPermissions = [];
        } else {
          // For other filters, sync with actual selections that are visible in current filter
          const filteredIds = this.getFilteredPermissionIds();
          const visibleSelections = this.selectedPermissions.filter(id => filteredIds.includes(id));

          // Ensure all IDs are strings for consistent comparison
          this.treeSelectedPermissions = visibleSelections.map(id => id.toString());
        }
      });
    },
    getFilteredPermissionIds() {
      // Get all permission IDs that are visible in the current filtered tree
      const extractIds = (nodes) => {
        if (!nodes) return [];
        const ids = [];
        nodes.forEach(node => {
          if (node.isLeaf) {
            ids.push(node.id.toString());
          }
          if (node.children && node.children.length > 0) {
            ids.push(...extractIds(node.children));
          }
        });
        return ids;
      };
      return extractIds(this.filteredPermissionsTree);
    },
    toggleSelectAll() {
      const visiblePermissionIds = this.getFilteredPermissionIds();
      const hasVisibleSelections = visiblePermissionIds.some(id => this.selectedPermissions.includes(id));
      
      if (hasVisibleSelections) {
        // Deselect all visible permissions
        this.selectedPermissions = this.selectedPermissions.filter(id => !visiblePermissionIds.includes(id));
      } else {
        // Select all visible permissions
        const selectionsFromOtherFilters = this.selectedPermissions.filter(id => !visiblePermissionIds.includes(id));
        this.selectedPermissions = [...selectionsFromOtherFilters, ...visiblePermissionIds];
      }
      
      // Update tree selections
      this.$nextTick(() => {
        this.syncTreeSelections();
      });

      // Emit the change
      this.$emit('selection-change', this.selectedPermissions);
    },
    toggleExpandAll() {
      if (this.allExpanded) {
        // Collapse all - empty the open nodes array
        this.openNodes = [];
        this.allExpanded = false;
      } else {
        // Expand all - add all parent node IDs to open nodes array
        this.openNodes = this.getAllParentNodeIds();
        this.allExpanded = true;
      }
    },
    getAllParentNodeIds() {
      const parentIds = [];
      const extractParentIds = (nodes) => {
        if (!nodes) return;
        nodes.forEach(node => {
          if (node.children && node.children.length > 0) {
            parentIds.push(node.id);
            extractParentIds(node.children);
          }
        });
      };
      extractParentIds(this.filteredPermissionsTree);
      return parentIds;
    },
    initializeOpenNodes() {
      // Initialize with all nodes expanded
      this.openNodes = this.getAllParentNodeIds();
      this.allExpanded = true;
    },
    updateOpenNodes(openNodes) {
      this.openNodes = openNodes;
      // Update allExpanded state based on whether all parent nodes are open
      const allParentIds = this.getAllParentNodeIds();
      this.allExpanded = allParentIds.length > 0 && allParentIds.every(id => openNodes.includes(id));
    }
  }
};
</script>

<style scoped>
.permissions-tree {
  height: 100%;
  overflow-y: auto;
}

.tree-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  min-height: 300px;
  max-height: calc(100vh - 250px);
  overflow-y: auto;
}

/* Add proper indentation for tree nodes */
.permissions-tree >>> .v-treeview-node__children {
  margin-left: 24px;
}

.permissions-tree >>> .v-treeview-node__content {
  padding-left: 12px;
}

.permissions-tree >>> .v-treeview-node__level-1 .v-treeview-node__content {
  padding-left: 16px;
}

.permissions-tree >>> .v-treeview-node__level-2 .v-treeview-node__content {
  padding-left: 24px;
}

.permissions-tree >>> .v-treeview-node__level-3 .v-treeview-node__content {
  padding-left: 32px;
}
</style>
